import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/pfc";
import { Check, Crown, Sparkles, Trophy } from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  spotlight: Sparkles,
  sponsored: Trophy,
  cover: Crown,
};

const FEATURES = {
  spotlight: [
    "Premium Directory placement (30 days)",
    "'Featured' gold badge across PFC",
    "Priority editorial review",
    "Social share kit",
  ],
  sponsored: [
    "1,200-word PFC editorial article",
    "Published in The Magazine",
    "Featured in Breaking News ticker",
    "Social campaign across PFC channels",
    "Permanent URL + SEO optimization",
  ],
  cover: [
    "Hero cover feature on the homepage",
    "Print + digital magazine cover treatment",
    "Multi-channel social campaign",
    "Professional photo direction (remote)",
    "PR distribution to partner outlets",
    "Cover-story video teaser",
  ],
};

export default function Pricing() {
  const [pkgs, setPkgs] = useState([]);
  const [loadingKey, setLoadingKey] = useState(null);
  const [subCount, setSubCount] = useState(null);

  useEffect(() => {
    api.get("/packages").then((r) => setPkgs(r.data));
    api.get("/newsletter/stats").then((r) => setSubCount(r.data.confirmed_subscribers)).catch(() => {});
  }, []);

  const checkout = async (key) => {
    setLoadingKey(key);
    try {
      const { data } = await api.post("/checkout/session", {
        package_id: key,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoadingKey(null);
    }
  };

  return (
    <div data-testid="pricing-page">
      <section className="relative py-20 border-b border-white/5 splatter-blue grain">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10 relative">
          <div className="label text-pfc-gold mb-3">Featured Packages</div>
          <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl text-white uppercase leading-[0.9]">
            Get on the <span className="gold-text">Cover</span>.<br />
            <span className="silver-text">Build the Brand.</span>
          </h1>
          <p className="text-white/65 mt-6 font-body text-lg max-w-2xl">
            Three ways to get featured on Public Figures Campaign. One-time payment. No subscriptions. No hidden fees.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="pricing-grid">
          {pkgs.map((p, i) => {
            const Icon = ICONS[p.key] || Sparkles;
            const isFeatured = p.key === "sponsored";
            return (
              <div
                key={p.key}
                className={`pfc-card relative p-8 lg:p-10 flex flex-col ${isFeatured ? "lg:scale-105 border-pfc-gold/50" : ""}`}
                data-testid={`pricing-card-${p.key}`}
              >
                {isFeatured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 label bg-pfc-gold text-ink-900 px-3 py-1.5">Most Popular</span>
                )}
                <Icon className="text-pfc-gold mb-5" size={32} />
                <div className="label text-white/60 mb-2">Package {String.fromCharCode(65 + i)}</div>
                <h3 className="font-display text-3xl text-white uppercase">{p.name}</h3>
                <p className="text-white/60 mt-3 font-body text-sm">{p.description}</p>
                {p.key === "sponsored" && subCount !== null && (
                  <div className="mt-4 pt-4 border-t border-white/10" data-testid="sponsored-reach">
                    <p className="label text-pfc-gold">Reach</p>
                    <p className="font-display text-2xl text-white mt-1">{subCount.toLocaleString()} <span className="font-sub text-xs text-white/50 uppercase tracking-[0.2em]">newsletter subscribers</span></p>
                  </div>
                )}

                <div className="mt-8 mb-8">
                  <span className="font-display text-6xl gold-text">${Math.floor(p.amount)}</span>
                  <span className="font-sub uppercase tracking-[0.2em] text-xs text-white/50 ml-2">USD · one-time</span>
                </div>

                <ul className="space-y-3 mb-10 flex-1">
                  {(FEATURES[p.key] || []).map((f) => (
                    <li key={f} className="flex items-start gap-3 text-white/80 font-body text-sm">
                      <Check className="text-pfc-gold shrink-0 mt-0.5" size={16} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => checkout(p.key)}
                  disabled={loadingKey === p.key}
                  className={`w-full font-sub uppercase tracking-[0.25em] text-sm py-4 transition disabled:opacity-50 ${
                    isFeatured
                      ? "bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright"
                      : "border border-white/20 text-white hover:border-pfc-gold hover:text-pfc-gold"
                  }`}
                  data-testid={`checkout-${p.key}`}
                >
                  {loadingKey === p.key ? "Redirecting…" : `Get ${p.name}`}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-20 text-center" data-testid="pricing-faq">
          <div className="label text-pfc-gold mb-3">Payment</div>
          <h3 className="font-display text-3xl text-white uppercase mb-3">Secure Checkout via <span className="gold-text">Stripe</span></h3>
          <p className="text-white/60 font-body max-w-xl mx-auto">
            All packages are billed in USD as a one-time charge. After payment, our editorial team will reach out within 1 business day to start your campaign.
          </p>
        </div>
      </section>
    </div>
  );
}
